import React, { useState, useEffect, useMemo } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react"

const GET_PROGRAM_REPORT = gql`
  query GetProgramReport($report: String!, $format: String!) {
    getProgramReport(report: $report, format: $format) {
      documentType
      documentData
    }
  }
`;

interface ViewReportProps {
  report: any;
  format: string;
  onClose: () => void;
  loading: boolean;
}

export const ViewReport: React.FC<ViewReportProps> = ({ report : viewData, loading, onClose }) => {
  const [headers, setHeaders] = useState<string[]>([])
  const [sortColumn, setSortColumn] = useState<string>('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (viewData.length > 0) {
      setHeaders(Object.keys(viewData[0]));
    }
  }, [viewData]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return viewData;
    return viewData.filter((row: any) =>
      Object.values(row).some((value: any) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [viewData, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    
    return [...filteredData].sort((a: any, b: any) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (aValue === bValue) return 0;
      
      const comparison = aValue < bValue ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortColumn, sortDirection]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
  };

  if (loading) return (  
    <Dialog
      open={true}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="fixed inset-0 flex items-center justify-center p-4 min-w-80 min-h-80 max-w-none"
        overlayClassName="bg-black/50"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          <p className="text-sm text-gray-500">Loading report data...</p>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog
      open={true}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="fixed inset-0 flex items-center justify-center p-4 min-w-80 min-h-80 max-w-none"
        overlayClassName="bg-black/50"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="bg-white rounded-lg shadow-xl w-[95%] h-[95%] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold">View Report Data</h2>
        </div>
        
        <div className="p-6 border-b">
          <div className="flex gap-4 items-center">
            <input
              type="text"
              placeholder="Search data..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <span className="text-sm text-gray-600">
              Showing {paginatedData.length} of {sortedData.length} entries
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {viewData.length > 0 ? (
            <table className="min-w-full table-auto divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  {headers.map(header => (
                    <th
                      key={header}
                      onClick={() => handleSort(header)}
                      className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                    >
                      <div className="flex items-center gap-1">
                        <span>{header}</span>
                        {getSortIcon(header)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedData.map((row: any, idx: number) => (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                  >
                    {headers.map(header => (
                      <td key={header} className="px-4 py-2 text-gray-600">
                        {typeof row[header] === 'object'
                          ? JSON.stringify(row[header])
                          : String(row[header] || '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">No data to display.</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewReport;
